import { useState } from "react";
import { Printer } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Skeleton } from "@/components/ui/Skeleton";
import { useInvoices } from "@/hooks/useInvoices";
import { formatPLN } from "@/lib/format";
import { BillingPeriod, Invoice } from "@/types/billing";
import { LineItemsTable } from "./LineItemsTable";

export function ClosedView({ period }: { period: BillingPeriod }) {
  const { data: invoices, loading } = useInvoices(period.period_id);
  const [open, setOpen] = useState<Invoice | null>(null);

  if (loading || !invoices) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex items-baseline justify-between">
          <div>
            <h2 className="text-lg font-semibold">Miesiąc zamknięty</h2>
            <p className="text-xs text-text-muted">
              Wysłano {invoices.filter((i) => i.status === "sent").length} z {invoices.length} faktur ·
              {period.closed_at && ` zamknięto ${period.closed_at.slice(0, 10)}`}
            </p>
          </div>
        </div>
      </Card>

      <Card padding="none">
        <table className="w-full text-sm">
          <thead className="text-xs text-text-muted">
            <tr className="border-b border-border">
              <th className="text-left font-medium px-4 py-2">Najemca</th>
              <th className="text-left font-medium px-4 py-2">Lokal</th>
              <th className="text-right font-medium px-4 py-2">Kwota</th>
              <th className="text-left font-medium px-4 py-2">Wysłano</th>
              <th className="px-4 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.invoice_id} className="border-b border-border last:border-0 hover:bg-bg-subtle cursor-pointer"
                  onClick={() => setOpen(inv)}>
                <td className="px-4 py-2 text-xs">{inv.tenant_name}</td>
                <td className="px-4 py-2 text-xs text-text-muted capitalize">{inv.location}</td>
                <td className="px-4 py-2 text-xs text-right font-mono">{formatPLN(inv.total_amount)}</td>
                <td className="px-4 py-2 text-text-muted font-mono text-xs">{inv.sent_at?.slice(0, 10) ?? "—"}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" iconLeft={<Printer className="size-3.5" />} onClick={(e) => { e.stopPropagation(); setOpen(inv); }}>
                    Drukuj
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {open && (
        <Modal
          open={true}
          onClose={() => setOpen(null)}
          title={`${open.tenant_name} · ${open.location}`}
          size="lg"
          footer={
            <Button variant="secondary" onClick={() => window.print()}>Drukuj</Button>
          }
        >
          <LineItemsTable items={open.line_items} />
          <div className="border-t border-border mt-3 pt-3 flex justify-between text-sm">
            <span className="text-text-muted">Suma</span>
            <span className="font-mono font-semibold">{formatPLN(open.total_amount)}</span>
          </div>
        </Modal>
      )}
    </div>
  );
}
